/**
 * @file AppearancePanel.tsx
 * @description Панель «Оформление сайта» (phase19.16 Р1; SB8–SB11, SB89;
 * uiux-design-r1 §1): вкладки «Цвета», «Шрифты», «Кнопки и карточки»,
 * «Фон и рамка», признак сохранения. Для нового магазина — полоса «Сейчас у сайта
 * вид по умолчанию…». «Начать с вида по умолчанию» — по сигналу снаружи
 * (пункт «⋯» экрана), с подтверждением.
 *
 * Самостоятельная панель без маршрута и без шапки страницы: документ
 * оформления — общая часть каркаса (`useSiteDraftPart("theme")`),
 * предпросмотр рисует тот, кто ставит панель (страница сейчас, оболочка
 * «Сайт → Конструктор» потом, фаза §7.14).
 */

import { useEffect, useRef, useState, type ReactNode } from "react";

import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { Tabs } from "../../ui/Tabs";
import { useConfirm } from "../../ui/use-confirm";
import { SaveIndicator } from "../SaveIndicator";
import { UnsavedGuard } from "../UnsavedGuard";
import { BackdropTab } from "./BackdropTab";
import { ColorsTab } from "./ColorsTab";
import { FontsTab } from "./FontsTab";
import { ShapesTab } from "./ShapesTab";
import { useSiteDraftPart } from "../../../hooks/site-view/useSiteDraftParts";
import { readDensity, withDensity } from "../../../lib/site-view/theme-extras";
import { THEME_RESET_DESCRIPTION, THEME_RESET_TITLE, themeResetKeepingShell } from "../../../lib/site-view/theme-reset";
import type { SiteViewDraft } from "../../../lib/site-view/types";

export type AppearanceTab = "colors" | "fonts" | "shapes" | "backdrop" | "designs";

const BASE_TABS: Array<{ value: AppearanceTab; label: string }> = [
  { value: "colors", label: "Цвета" },
  { value: "fonts", label: "Шрифты" },
  { value: "shapes", label: "Кнопки и карточки" },
  { value: "backdrop", label: "Фон и рамка" },
];

interface Props {
  draft: SiteViewDraft;
  /** Счётчик «Начать с вида по умолчанию»: рост — спросить и заменить. */
  resetSignal?: number;
  /** Вкладку задаёт конструктор (выбор в адресе). */
  tab?: AppearanceTab;
  onTab?: (tab: AppearanceTab) => void;
  /** Вкладка «Дизайны» (§9 проекта оболочки) — её содержимое даёт конструктор. */
  designs?: ReactNode;
}

export function AppearancePanel({ draft, resetSignal = 0, tab: outerTab, onTab, designs }: Props) {
  const [ownTab, setOwnTab] = useState<AppearanceTab>("colors");
  const tab = outerTab ?? ownTab;
  const setTab = onTab ?? setOwnTab;
  const tabs = designs ? [...BASE_TABS, { value: "designs" as const, label: "Дизайны" }] : BASE_TABS;
  const themeDraft = useSiteDraftPart("theme");
  const { theme, update } = themeDraft;
  const confirm = useConfirm();
  // Свежие функции без перезапуска эффекта: окно — только по сигналу.
  const latest = useRef({ ask: confirm.confirm, replace: themeDraft.replace, theme });
  latest.current = { ask: confirm.confirm, replace: themeDraft.replace, theme };

  useEffect(() => {
    if (resetSignal === 0) return;
    const { ask } = latest.current;
    void ask({
      title: THEME_RESET_TITLE,
      description: THEME_RESET_DESCRIPTION,
      action: "Заменить",
      danger: true,
    }).then((ok) => {
      // Шапку, подвал и вид корзины не трогаем: у них свои панели (SB120 этап 4).
      if (ok) latest.current.replace(themeResetKeepingShell(latest.current.theme));
    });
  }, [resetSignal]);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {draft.published === null && !draft.parts.THEME.changed ? (
        <NoticeStrip>
          Сейчас у сайта вид по умолчанию: белый фон, тёмный текст, системный шрифт. Начните с цвета кнопок и шрифта
          заголовков.
        </NoticeStrip>
      ) : null}
      {/* Одна строка с прокруткой вбок (макет uiux-fonts-usage §3): выбранная вкладка сама прокручивается в видимую часть. */}
      <Tabs aria-label="Разделы оформления" items={tabs} value={tab} onChange={setTab} />
      <SaveIndicator state={themeDraft} />
      {tab === "colors" ? <ColorsTab colors={theme.colors} onChange={(role, hex) => update("colors", { [role]: hex })} /> : null}
      {tab === "fonts" ? (
        <FontsTab
          theme={theme}
          onFonts={(patch) => update("fonts", patch)}
          onType={(patch) => update("type", patch)}
          onOpenDesigns={designs ? () => setTab("designs") : undefined}
        />
      ) : null}
      {tab === "shapes" ? (
        <ShapesTab
          theme={theme}
          onShapes={(patch) => update("shapes", patch)}
          onSpacing={(patch) => {
            // Плотность — через readDensity (битое → 1) и withDensity (в пределах), доля телефона — как пришла.
            const merged = { ...theme, spacing: { ...theme.spacing, ...patch } };
            themeDraft.change(withDensity(merged, readDensity(merged)), ["spacing"]);
          }}
        />
      ) : null}
      {tab === "designs" ? designs : null}
      {tab === "backdrop" ? (
        <BackdropTab theme={theme} onBackdrop={(patch) => update("backdrop", patch)} onFrame={(patch) => update("frame", patch)} />
      ) : null}
      <UnsavedGuard when={themeDraft.status === "error" || themeDraft.status === "conflict"} />
      <ConfirmDialog request={confirm.request} onSettle={confirm.settle} />
    </div>
  );
}
